import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CollaborationStatus } from '../../../../API';
import { CollaborationDataService } from '../../services/collaboration-data.service';
import { AT_LEAST_ONE_ALPHABET, NOT_ALLOWED_SPECIAL_CHARS, NOT_ONLY_WHITESPACE } from '../../../shared/utils/constants';

@Component({
  selector: 'app-collaboration-details',
  templateUrl: './collaboration-details.component.html',
  styleUrl: './collaboration-details.component.scss',
})
export class CollaborationDetailsComponent implements OnInit {
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancelBtn = new EventEmitter<void>();

  collaborationForm!: FormGroup;

  constructor(
    private fb: FormBuilder,
    private nzMessageService: NzMessageService,
    public collaborationDataService: CollaborationDataService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadExistingData();
    this.collaborationDataService.errorMessage = '';

    // Update validation state in service whenever form validity changes
    this.collaborationForm.statusChanges.subscribe(() => {
      this.collaborationDataService.isStep1Valid = this.collaborationForm.valid;
    });

    // Set initial validation state
    this.collaborationDataService.isStep1Valid = this.collaborationForm.valid;
  }

  private initializeForm(): void {
    this.collaborationForm = this.fb.group({
      name: [
        '',
        [
          Validators.required,
          Validators.maxLength(40),
          Validators.pattern(AT_LEAST_ONE_ALPHABET),
          Validators.pattern(NOT_ALLOWED_SPECIAL_CHARS),
        ],
      ],
      description: ['', [Validators.required, Validators.maxLength(100), Validators.pattern(NOT_ONLY_WHITESPACE)]],
      objective: ['', [Validators.required, Validators.maxLength(1000), Validators.pattern(NOT_ONLY_WHITESPACE)]],
      partnerOrgType: [''],
      partnerEmail: [[]],
      status: [CollaborationStatus.DRAFT],
      lastModifiedBy: [''],
      lastModifiedAt: [''],
      createdBy: [''],
      createdAt: [''],
      ownerDatasets: [{}],
      partnerDatasets: [{}],
      partnerInviteMessage: [''],
    });
  }

  private loadExistingData(): void {
    this.collaborationDataService.collaborationData$.subscribe((collaborationDetails) => {
      if (collaborationDetails) {
        this.collaborationForm.patchValue({
          name: collaborationDetails.name || '',
          description: collaborationDetails.description || '',
          objective: collaborationDetails.objective || '',
          partnerOrgType: collaborationDetails.partnerOrgType || '',
          partnerEmail: collaborationDetails.partnerEmail || [],
          status: collaborationDetails.status || CollaborationStatus.DRAFT,
          lastModifiedBy: collaborationDetails.lastModifiedBy || '',
          lastModifiedAt: collaborationDetails.lastModifiedAt || '',
          createdBy: collaborationDetails.createdBy || '',
          ownerDatasets: collaborationDetails.ownerDatasets || [],
          partnerDatasets: collaborationDetails.partnerDatasets || [],
          partnerInviteMessage: collaborationDetails.partnerInviteMessage || '',
        });
        // Update validation state after loading data
        this.collaborationDataService.isStep1Valid = this.collaborationForm.valid;
      }
    });
  }

  onCancel(): void {
    this.cancelBtn.emit();
  }

  onSaveAsDraft(): void {
    if (this.collaborationForm.valid) {
      const formData = this.collaborationForm.value;
      this.collaborationDataService.updateCollaborationDetails(formData);
    } else {
      this.nzMessageService.error('Please fill in all required fields before saving as draft.');
    }
  }

  onNext(): void {
    if (this.collaborationForm.valid) {
      this.collaborationDataService.currentStep = 1;
      this.nextStep.emit();
    }
  }

  reset(): void {
    const original = this.collaborationDataService.originalCollaborationData;
    this.collaborationForm.patchValue({
      name: original?.name || '',
      description: original?.description || '',
      objective: original?.objective || '',
    });
    this.collaborationDataService.updateCollaborationDetails({
      name: original?.name || '',
      description: original?.description || '',
      objective: original?.objective || '',
    });
  }
}
